'use client';

import { useState, useEffect, useRef } from 'react';
import { SearchIcon } from '@/assets/icons';
import { cn } from '@/lib/utils';
import { Select } from '@/components/FormElements/select';
import { getTrips, Trip } from '@/lib/trips/api';

export interface TripSearchParams {
  destination?: string;
  startDate?: string;
  duration?: number;
  tripType?: 'Join-In' | 'Private';
  adults?: number;
  children?: number;
}

interface TripSearchFormProps {
  onSearch?: (params: TripSearchParams) => void;
  className?: string;
}

const DURATION_OPTIONS = [
  { value: '1', label: '1 Day' },
  { value: '2-3', label: '2-3 Days' },
  { value: '4-7', label: '4-7 Days' },
  { value: '8-14', label: '8-14 Days' },
  { value: '15+', label: '15+ Days' },
];

const TRIP_TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'Join-In', label: 'Join-In' },
  { value: 'Private', label: 'Private' },
];

export default function TripSearchForm({ onSearch, className }: TripSearchFormProps) {
  const [destination, setDestination] = useState('');
  const [startDate, setStartDate] = useState('');
  const [duration, setDuration] = useState('');
  const [tripType, setTripType] = useState('');
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  
  // Autocomplete state
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState<Trip[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const autocompleteRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close autocomplete when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (autocompleteRef.current && !autocompleteRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch destination suggestions
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (destination.length < 2) {
        setSuggestions([]);
        return;
      }

      setLoadingSuggestions(true);
      try {
        const response = await getTrips(1, 10, 'created_at', 'DESC', destination);
        if (response?.data) {
          // Filter unique destinations
          const uniqueDestinations = response.data.filter(
            (trip, index, self) =>
              index === self.findIndex((t) => t.location?.location_name === trip.location?.location_name)
          );
          setSuggestions(uniqueDestinations);
        }
      } catch (error) {
        console.error('Error fetching suggestions:', error);
      } finally {
        setLoadingSuggestions(false);
      }
    };

    const debounce = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(debounce);
  }, [destination]);

  const handleSuggestionClick = (locationName: string) => {
    setDestination(locationName);
    setShowSuggestions(false);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    
    const params: TripSearchParams = {};
    if (destination) params.destination = destination;
    if (startDate) params.startDate = startDate;
    if (duration) params.duration = parseDurationValue(duration);
    if (tripType) params.tripType = tripType as 'Join-In' | 'Private';
    if (adults > 1) params.adults = adults;
    if (children > 0) params.children = children;

    onSearch?.(params);
  };

  const parseDurationValue = (value: string): number | undefined => {
    switch (value) {
      case '1': return 1;
      case '2-3': return 3;
      case '4-7': return 7;
      case '8-14': return 14;
      case '15+': return 15;
      default: return undefined;
    }
  };

  const incrementCount = (type: 'adults' | 'children', max?: number) => {
    if (type === 'adults') {
      if (max === undefined || adults < max) setAdults((prev) => prev + 1);
    } else {
      if (max === undefined || children < max) setChildren((prev) => prev + 1);
    }
  };

  const decrementCount = (type: 'adults' | 'children') => {
    if (type === 'adults' && adults > 1) {
      setAdults((prev) => prev - 1);
    } else if (type === 'children' && children > 0) {
      setChildren((prev) => prev - 1);
    }
  };

  const handleReset = () => {
    setDestination('');
    setStartDate('');
    setDuration('');
    setTripType('');
    setAdults(1);
    setChildren(0);
    onSearch?.({});
  };

  return (
    <div className={cn('rounded-[10px] bg-white shadow-1 dark:bg-gray-dark dark:shadow-card', className)}>
      <form onSubmit={handleSearch} className="p-4 sm:p-6 xl:p-10">
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {/* Destination Autocomplete */}
          <div className="relative" ref={autocompleteRef}>
            <label className="mb-3 block text-body-sm font-medium text-dark dark:text-white">
              Destination
            </label>
            <div className="relative">
              <input
                ref={inputRef}
                type="text"
                value={destination}
                onChange={(e) => setDestination(e.target.value)}
                onFocus={() => setShowSuggestions(true)}
                placeholder="Search destination..."
                className="w-full rounded-lg border-[1.5px] border-stroke bg-transparent px-5 py-3 outline-none transition focus:border-primary active:border-primary dark:border-dark-3 dark:bg-dark-2 dark:focus:border-primary"
              />
              
              {/* Suggestions Dropdown */}
              {showSuggestions && (destination.length >= 2) && (
                <div className="absolute z-50 mt-1 w-full rounded-lg border border-stroke bg-white shadow-lg dark:border-dark-3 dark:bg-dark-2">
                  {loadingSuggestions ? (
                    <div className="px-5 py-3 text-sm text-gray-500 dark:text-gray-400">
                      Loading...
                    </div>
                  ) : suggestions.length > 0 ? (
                    <ul className="max-h-60 overflow-auto py-1">
                      {suggestions.map((trip) => (
                        <li
                          key={trip.id}
                          onClick={() => handleSuggestionClick(trip.location?.location_name || '')}
                          className="cursor-pointer px-5 py-2.5 text-sm hover:bg-gray-2 dark:hover:bg-dark dark:text-white"
                        >
                          {trip.location?.location_name}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="px-5 py-3 text-sm text-gray-500 dark:text-gray-400">
                      No destinations found
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Start Date */}
          <div>
            <label className="mb-3 block text-body-sm font-medium text-dark dark:text-white">
              Start Date
            </label>
            <div className="relative">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="w-full rounded-lg border-[1.5px] border-stroke bg-transparent px-5 py-3 outline-none transition focus:border-primary active:border-primary dark:border-dark-3 dark:bg-dark-2 dark:focus:border-primary dark:text-white"
              />
            </div>
          </div>

          {/* Duration */}
          <div>
            <Select
              label="Duration"
              placeholder="Select duration"
              items={DURATION_OPTIONS}
              value={duration}
              onChange={setDuration}
            />
          </div>

          {/* Trip Type */}
          <div>
            <Select
              label="Trip Type"
              placeholder="All Types"
              items={TRIP_TYPE_OPTIONS}
              value={tripType}
              onChange={setTripType}
            />
          </div>

          {/* Adults Counter */}
          <div>
            <label className="mb-3 block text-body-sm font-medium text-dark dark:text-white">
              Adults
            </label>
            <div className="flex items-center rounded-lg border-[1.5px] border-stroke dark:border-dark-3">
              <button
                type="button"
                onClick={() => decrementCount('adults')}
                disabled={adults <= 1}
                className="flex h-12 w-12 items-center justify-center text-dark hover:bg-gray-2 disabled:cursor-not-allowed disabled:opacity-50 dark:text-white dark:hover:bg-dark"
              >
                <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                </svg>
              </button>
              <div className="flex flex-1 items-center justify-center">
                <svg className="mr-2 size-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span className="text-center font-medium text-dark dark:text-white">
                  {adults}
                </span>
              </div>
              <button
                type="button"
                onClick={() => incrementCount('adults')}
                className="flex h-12 w-12 items-center justify-center text-dark hover:bg-gray-2 dark:text-white dark:hover:bg-dark"
              >
                <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>
            </div>
          </div>

          {/* Children Counter */}
          <div>
            <label className="mb-3 block text-body-sm font-medium text-dark dark:text-white">
              Children
            </label>
            <div className="flex items-center rounded-lg border-[1.5px] border-stroke dark:border-dark-3">
              <button
                type="button"
                onClick={() => decrementCount('children')}
                disabled={children <= 0}
                className="flex h-12 w-12 items-center justify-center text-dark hover:bg-gray-2 disabled:cursor-not-allowed disabled:opacity-50 dark:text-white dark:hover:bg-dark"
              >
                <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                </svg>
              </button>
              <div className="flex flex-1 items-center justify-center">
                <svg className="mr-2 size-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span className="text-center font-medium text-dark dark:text-white">
                  {children}
                </span>
              </div>
              <button
                type="button"
                onClick={() => incrementCount('children')}
                className="flex h-12 w-12 items-center justify-center text-dark hover:bg-gray-2 dark:text-white dark:hover:bg-dark"
              >
                <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <button
            type="submit"
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-6 py-3 font-medium text-white hover:bg-opacity-90 transition duration-200 ease-in-out"
          >
            <SearchIcon className="size-5" />
            Search
          </button>
          <button
            type="button"
            onClick={handleReset}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-stroke px-6 py-3 font-medium text-dark hover:bg-gray-2 dark:border-dark-3 dark:text-white dark:hover:bg-dark transition duration-200 ease-in-out"
          >
            Reset
          </button>
        </div>
      </form>
    </div>
  );
}
